import { format } from 'date-fns';

import { ALL_INTENTS, NA } from './constants';
import i18n from 'src/locales';
import {
  AllConversationChannel,
  ConversationChannel,
  IntentData,
} from 'src/types/workflowBuilderAPITypes';
import {
  constructTimeRangeQueryParamsRevamped,
  last30DaysTimeRange,
} from 'src/utils/timeRangeHelpers';

export function formatCSAT(score?: number | null) {
  if (typeof score !== 'number') {
    return NA;
  }
  return score.toFixed(1);
}

export function openTab(url: string) {
  window.open(url, '_blank');
}

/**
 * create a url to be opened in a new tab on workflow builder landing page with initialPhrase q param
 */
export function getCreateIntentUrl(
  initialIntentPhrase: string,
  channel?: ConversationChannel,
) {
  const searchParams = new URLSearchParams(window.location.search);
  let currParams = Object.fromEntries(searchParams);

  // current search params doesn't contain the needed params, use the default params
  if (
    !searchParams.has('start') ||
    !searchParams.has('end') ||
    !searchParams.has('channel')
  ) {
    currParams = Object.fromEntries(
      constructTimeRangeQueryParamsRevamped(last30DaysTimeRange, 'widget'),
    );
  }
  const qString = new URLSearchParams({
    ...currParams,
    header: 'workflows',
    initialIntentPhrase,
  });
  if (channel) {
    qString.set('channel', channel);
  }

  return `${window.location.origin}/workflow-builder?${qString}`;
}

/**
 * create a url to be opened in a new tab on workflow builder edit page with initialPhrase q param
 */
export function getWorkflowBuilderUrl({
  channel,
  emailConfigurationId,
  emailMode,
  header,
  initialIntentPhrase,
  intentDefinitionId,
  intentWorkflowId,
  mode,
  stepId,
  stepWorkflowId,
  version,
  versionType,
}: {
  channel?: AllConversationChannel;
  emailConfigurationId?: string;
  emailMode?: string;
  header?: string;
  initialIntentPhrase?: string;
  intentDefinitionId?: string;
  intentWorkflowId: string;
  mode?: string;
  stepId?: string;
  stepWorkflowId?: string;
  version?: number;
  versionType?: string;
}) {
  const qString = new URLSearchParams();
  if (intentDefinitionId) {
    qString.set('intentId', intentDefinitionId);
  }
  if (channel) {
    if (channel === 'sunshine' || channel === 'preview') {
      qString.set('view', 'widget');
    } else {
      qString.set('view', channel);
    }
  }
  if (initialIntentPhrase) {
    qString.set('initialIntentPhrase', initialIntentPhrase);
  }
  if (mode) {
    qString.set('mode', mode);
  }
  if (emailMode) {
    qString.set('emailMode', emailMode);
  }
  if (stepId) {
    qString.set('stepId', stepId);
  }
  if (stepWorkflowId) {
    qString.set('stepWorkflowId', stepWorkflowId);
  }
  if (emailConfigurationId) {
    qString.set('configurationId', emailConfigurationId);
  }

  if (intentWorkflowId) {
    qString.set('workflowId', intentWorkflowId);
  }
  if (version !== undefined) {
    qString.set('version', String(version));
  }
  if (versionType) {
    qString.set('versionType', versionType);
  }
  if (header) {
    qString.set('header', header);
  }
  return `${window.location.origin}/workflow-builder-edit?${qString}`;
}

const t = i18n.getFixedT(null, 'intent-conversation-analytics');

export function getIntentOptions({
  excludeAllIntentsOption,
  intents,
}: {
  excludeAllIntentsOption?: boolean;
  intents: IntentData[];
}) {
  const allIntentsOption = {
    label: t('helpers.allIntents'),
    value: ALL_INTENTS,
  };
  const intentOptions = intents.map(intent => ({
    label: intent.intent_name,
    value: intent.intent_definition_id,
  }));

  if (excludeAllIntentsOption) {
    return intentOptions;
  }
  return [allIntentsOption, ...intentOptions];
}

export const multiIntentOption = (intents: IntentData[]) => {
  return {
    label: t('helpers.allAgents'),
    options: intents.map(intent => ({
      label: intent.intent_name,
      value: `intent_ids.${intent.intent_definition_id}`,
    })),
    value: 'intent_ids',
  };
};

// Formats timestamp as date like 'Jun 25, 2024 3:14:02 pm'
export const formatTimestamp = (timestamp: string) => {
  return format(new Date(timestamp), 'MMM dd, yyyy h:mm:ss aaa');
};
